import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Chip, CircularProgress, Alert, Button, Avatar, Stack, Grid, TextField,
    FormControl, InputLabel, Select, MenuItem, OutlinedInput
} from '@mui/material';
import {
    Edit, Save, Cancel, AssignmentInd, Description, InsertPhoto, Link as LinkIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import ru from 'date-fns/locale/ru';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { taskService } from '../../services/taskService';
import { Task, TaskUpdatePayload } from '../../types/task';
import { TaskStatus } from '../../types/task-status';
import { User } from '../../types/user';
import { ManagerDTO } from '../../types/manager';
import { Category } from '../../types/category';
import { Skill } from '../../types/skill';
import { userService } from '../../services/userService';
import { managerService } from '../../services/managerService';
import { categoryService } from '../../services/categoryService';
import { skillService } from '../../services/skillService';
import { useAuth } from '../../contexts/AuthContext';
import { UserType, ManagerRole } from '../../types/auth';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const AdminTaskEntityDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { state } = useAuth();
    const authenticatedUser = state.user;

    const [task, setTask] = useState<Task | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState<boolean>(false);

    // States for editable fields
    const [editTitle, setEditTitle] = useState<string>('');
    const [editDescription, setEditDescription] = useState<string>('');
    const [editStatus, setEditStatus] = useState<TaskStatus | undefined>(undefined);
    const [editPrice, setEditPrice] = useState<string>('');
    const [editDeadline, setEditDeadline] = useState<Date | null>(null);
    const [editEstimatedHours, setEditEstimatedHours] = useState<string>('');
    const [editPriority, setEditPriority] = useState<string>('');
    const [editRequirements, setEditRequirements] = useState<string>('');
    const [editCompletionResult, setEditCompletionResult] = useState<string>('');
    const [editReviewComment, setEditReviewComment] = useState<string>('');
    const [selectedAssignedUserIds, setSelectedAssignedUserIds] = useState<string[]>([]); // Изменено на массив для множественного выбора
    const [editManagerId, setEditManagerId] = useState<string | undefined>(undefined);
    const [editNeededUsers, setEditNeededUsers] = useState<string>('');
    const [editCategoryId, setEditCategoryId] = useState<string | undefined>(undefined);
    const [selectedSkillIds, setSelectedSkillIds] = useState<string[]>([]);


    // Dropdown options
    const [availableUsers, setAvailableUsers] = useState<User[]>([]);
    const [availableManagers, setAvailableManagers] = useState<ManagerDTO[]>([]);
    const [availableCategories, setAvailableCategories] = useState<Category[]>([]);
    const [availableSkills, setAvailableSkills] = useState<Skill[]>([]);

    const isPlatformCuratorOrAdmin = authenticatedUser?.type === UserType.MANAGER && (authenticatedUser.manager?.roles?.includes(ManagerRole.PLATFORM_CURATOR) || authenticatedUser.manager?.roles?.includes(ManagerRole.ADMIN));

    const getStatusDisplayName = (status?: TaskStatus) => {
        switch (status) {
            case TaskStatus.Created: return 'Создана';
            case TaskStatus.Assigned: return 'Назначена';
            case TaskStatus.Started: return 'В работе';
            case TaskStatus.Completed: return 'Завершена';
            case TaskStatus.Review: return 'На проверке';
            case TaskStatus.Verified: return 'Проверена';
            case TaskStatus.ForRevision: return 'На доработку';
            case TaskStatus.Rejected: return 'Отклонена';
            case TaskStatus.Paid: return 'Оплачена';
            case TaskStatus.Cancelled: return 'Отменена';
            case TaskStatus.Archived: return 'В архиве';
            default: return status || 'Неизвестно';
        }
    };

    const getStatusChipColor = (status?: TaskStatus) => {
        switch (status) {
            case TaskStatus.Paid:
            case TaskStatus.Verified:
            case TaskStatus.Completed:
                return "success";
            case TaskStatus.Review:
            case TaskStatus.ForRevision:
            case TaskStatus.Assigned:
            case TaskStatus.Started:
                return "info";
            case TaskStatus.Rejected:
            case TaskStatus.Cancelled:
                return "error";
            default:
                return "default";
        }
    };

    useEffect(() => {
        const fetchTaskDetails = async () => {
            if (!id) {
                setError('Не удалось загрузить детали задачи: ID задачи отсутствует.');
                setLoading(false);
                return;
            }
            try {
                setLoading(true);
                const taskData = await taskService.getTaskById(id);
                setTask(taskData);

                // Initialize editable states
                setEditTitle(taskData.title);
                setEditDescription(taskData.description);
                setEditStatus(taskData.status);
                setEditPrice(taskData.priceInKopecks ? (taskData.priceInKopecks / 100).toString() : '');
                setEditDeadline(taskData.deadline ? new Date(taskData.deadline) : null);
                setEditEstimatedHours(taskData.estimatedHours?.toString() || '');
                setEditPriority(taskData.priority.toString());
                setEditRequirements(taskData.requirements || '');
                setEditCompletionResult(taskData.completionResult || '');
                setEditReviewComment(taskData.reviewComment || '');
                // Инициализируем выбранных исполнителей из task.taskUsers
                setSelectedAssignedUserIds(taskData.taskUsers?.map(tu => tu.userId!) || []);
                setEditManagerId(taskData.managerId || undefined);
                setEditNeededUsers(taskData.neededUsers?.toString() || '');
                setEditCategoryId(taskData.categoryId || undefined);
                setSelectedSkillIds(taskData.taskSkills?.map(ts => ts.skillId!) || []);


                // Fetch dropdown options
                const usersResponse = await userService.getAllUsers();
                setAvailableUsers(usersResponse);

                const managersResponse = await managerService.getAllManagers();
                setAvailableManagers(managersResponse);

                const categoriesResponse = await categoryService.getAllCategories();
                setAvailableCategories(categoriesResponse.data);

                const skillsResponse = await skillService.getAllSkills();
                setAvailableSkills(skillsResponse.data);

            } catch (err) {
                setError('Не удалось загрузить детали задачи.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchTaskDetails();
    }, [id]);

    const handleSave = async () => {
        if (!task || !id) return;
        try {
            const updatedTask: TaskUpdatePayload = {
                id: task.id,
                title: editTitle,
                description: editDescription,
                status: editStatus || TaskStatus.Created,
                priceInKopecks: editPrice ? parseFloat(editPrice) * 100 : 0,
                deadline: editDeadline ? editDeadline.toISOString() : undefined,
                estimatedHours: editEstimatedHours ? parseInt(editEstimatedHours) : undefined,
                priority: editPriority ? parseInt(editPriority) : 3,
                requirements: editRequirements,
                completionResult: editCompletionResult || undefined,
                reviewComment: editReviewComment || undefined,
                currentExecutorId: undefined, // Удалено, так как исполнители теперь в assignedUserIds
                managerId: editManagerId || undefined,
                neededUsers: editNeededUsers ? parseInt(editNeededUsers) : undefined,
                organizationId: task.organizationId,
                categoryId: editCategoryId || undefined,
                skillIds: selectedSkillIds, // Добавлено
                assignedUserIds: selectedAssignedUserIds, // Добавлено для списка ID назначенных исполнителей
            };

            await taskService.updateTask(id, updatedTask);
            const refreshedTask = await taskService.getTaskById(id);
            setTask(refreshedTask);
            setIsEditing(false);
        } catch (err) {
            setError('Не удалось сохранить изменения задачи.');
            console.error(err);
        }
    };

    const handleCancelEdit = () => {
        if (task) {
            setEditTitle(task.title);
            setEditDescription(task.description);
            setEditStatus(task.status);
            setEditPrice(task.priceInKopecks ? (task.priceInKopecks / 100).toString() : '');
            setEditDeadline(task.deadline ? new Date(task.deadline) : null);
            setEditEstimatedHours(task.estimatedHours?.toString() || '');
            setEditPriority(task.priority.toString());
            setEditRequirements(task.requirements || '');
            setEditCompletionResult(task.completionResult || '');
            setEditReviewComment(task.reviewComment || '');
            // Сбрасываем выбранных исполнителей при отмене
            setSelectedAssignedUserIds(task.taskUsers?.map(tu => tu.userId!) || []);
            setEditManagerId(task.managerId || undefined);
            setEditNeededUsers(task.neededUsers?.toString() || '');
            setEditCategoryId(task.categoryId || undefined);
            setSelectedSkillIds(task.taskSkills?.map(ts => ts.skillId!) || []);
        }
        setIsEditing(false);
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
    }

    if (error) {
        return <Box sx={{ mt: 4 }}><Alert severity="error">{error}</Alert></Box>;
    }

    if (!task) {
        return <Box sx={{ mt: 4 }}><Alert severity="info">Задача не найдена.</Alert></Box>;
    }

    const uniqueSkills = new Map<string, Skill>();
    if (task.taskSkills) {
        task.taskSkills.forEach(ts => {
            if (ts.skill && ts.skill.id && !uniqueSkills.has(ts.skill.id)) {
                uniqueSkills.set(ts.skill.id, ts.skill);
            }
        });
    }
    if (task.taskRequiredSkills) {
        task.taskRequiredSkills.forEach(trs => {
            if (trs.skill && trs.skill.id && !uniqueSkills.has(trs.skill.id)) {
                uniqueSkills.set(trs.skill.id, trs.skill);
            }
        });
    }
    const skillsToDisplay = Array.from(uniqueSkills.values());

    return (
        <Box sx={{ p: 3, maxWidth: 1200, margin: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        Дата создания: {task.createdAt ? format(new Date(task.createdAt), 'dd.MM.yyyy', { locale: ru }) : '—'}
                    </Typography>
                    {isEditing ? (
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="Название задачи"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            sx={{ mb: 2 }}
                        />
                    ) : (
                        <Typography variant="h4" component="h1" gutterBottom>{task.title}</Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {isEditing ? (
                        <TextField
                            label="Бюджет в рублях"
                            type="number"
                            value={editPrice}
                            onChange={(e) => setEditPrice(e.target.value)}
                            sx={{ mr: 2, width: 150 }}
                        />
                    ) : (
                        <Typography variant="h5" component="span" sx={{ mr: 2 }}>{task.priceInKopecks ? `${task.priceInKopecks / 100}₽` : '—'}</Typography>
                    )}
                    {isPlatformCuratorOrAdmin && !isEditing && (
                        <Button variant="contained" startIcon={<Edit />} onClick={() => setIsEditing(true)}>Редактировать задачу</Button>
                    )}
                    {isPlatformCuratorOrAdmin && isEditing && (
                        <Box>
                            <Button variant="outlined" startIcon={<Cancel />} onClick={handleCancelEdit} sx={{ mr: 1 }}>Отмена</Button>
                            <Button variant="contained" startIcon={<Save />} onClick={handleSave}>Сохранить Изменения</Button>
                        </Box>
                    )}
                </Box>
            </Box>

            <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
                <Grid container spacing={2}>
                    <Grid item xs={2}>
                        <Typography variant="body2" color="textSecondary">Категория</Typography>
                        {isEditing ? (
                            <FormControl fullWidth size="small">
                                <InputLabel id="edit-category-label">Категория</InputLabel>
                                <Select
                                    labelId="edit-category-label"
                                    value={editCategoryId || ''}
                                    label="Категория"
                                    onChange={(event) => setEditCategoryId(event.target.value as string)}
                                >
                                    <MenuItem value="">— Не выбрана —</MenuItem>
                                    {availableCategories.map((category) => (
                                        <MenuItem key={category.id} value={category.id}>
                                            {category.title}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : (
                            <Typography variant="subtitle1">{task.category?.title || '—'}</Typography>
                        )}
                    </Grid>
                    <Grid item xs={2}>
                        <Typography variant="body2" color="textSecondary">Статус</Typography>
                        {isEditing ? (
                            <FormControl fullWidth size="small">
                                <InputLabel id="edit-status-label">Статус</InputLabel>
                                <Select
                                    labelId="edit-status-label"
                                    value={editStatus || ''}
                                    label="Статус"
                                    onChange={(event) => setEditStatus(event.target.value as TaskStatus)}
                                >
                                    {Object.values(TaskStatus).map((status) => (
                                        <MenuItem key={status} value={status}>
                                            {getStatusDisplayName(status)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : (
                            <Typography variant="subtitle1">{getStatusDisplayName(task.status)}</Typography>
                        )}
                    </Grid>
                    <Grid item xs={2}>
                        <Typography variant="body2" color="textSecondary">Принятие</Typography>
                        <Typography variant="subtitle1">{task.assignedAt ? format(new Date(task.assignedAt), 'dd.MM.yyyy') : '—'} {/* Placeholder */}</Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <Typography variant="body2" color="textSecondary">Дедлайн</Typography>
                        {isEditing ? (
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label="Дедлайн"
                                    value={editDeadline}
                                    onChange={(newValue) => setEditDeadline(newValue)}
                                    slots={{ textField: (params) => <TextField {...params} fullWidth size="small" /> }}
                                />
                            </LocalizationProvider>
                        ) : (
                            <Typography variant="subtitle1">{task.deadline ? format(new Date(task.deadline), 'dd.MM.yyyy', { locale: ru }) : '—'}</Typography>
                        )}
                    </Grid>
                    <Grid item xs={4}>
                        <Typography variant="body2" color="textSecondary">Заказчик</Typography>
                        <Typography variant="subtitle1">{task.organization?.name || '—'}</Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <Typography variant="body2" color="textSecondary">Расчетное время (часы)</Typography>
                        {isEditing ? (
                            <TextField
                                fullWidth
                                variant="outlined"
                                type="number"
                                value={editEstimatedHours}
                                onChange={(e) => setEditEstimatedHours(e.target.value)}
                                size="small"
                            />
                        ) : (
                            <Typography variant="subtitle1">{task.estimatedHours || '—'}</Typography>
                        )}
                    </Grid>
                    <Grid item xs={2}>
                        <Typography variant="body2" color="textSecondary">Приоритет</Typography>
                        {isEditing ? (
                            <TextField
                                fullWidth
                                variant="outlined"
                                type="number"
                                inputProps={{ min: 1, max: 5 }}
                                value={editPriority}
                                onChange={(e) => setEditPriority(e.target.value)}
                                size="small"
                            />
                        ) : (
                            <Typography variant="subtitle1">{task.priority || '—'}</Typography>
                        )}
                    </Grid>
                    <Grid item xs={4}>
                        <Typography variant="body2" color="textSecondary">Требуемое количество исполнителей</Typography>
                        {isEditing ? (
                            <TextField
                                fullWidth
                                variant="outlined"
                                type="number"
                                value={editNeededUsers}
                                onChange={(e) => setEditNeededUsers(e.target.value)}
                                size="small"
                            />
                        ) : (
                            <Typography variant="subtitle1">{task.neededUsers || '—'}</Typography>
                        )}
                    </Grid>
                    <Grid item xs={4}>
                        <Typography variant="body2" color="textSecondary">Менеджер</Typography>
                        {isEditing ? (
                            <FormControl fullWidth size="small">
                                <InputLabel id="edit-manager-label">Менеджер</InputLabel>
                                <Select
                                    labelId="edit-manager-label"
                                    value={editManagerId || ''}
                                    label="Менеджер"
                                    onChange={(event) => setEditManagerId(event.target.value as string)}
                                >
                                    <MenuItem value="">— Не выбран —</MenuItem>
                                    {availableManagers.map((manager) => (
                                        <MenuItem key={manager.id} value={manager.id}>
                                            {manager.fullName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : (
                            <Typography variant="subtitle1">{task.manager?.fullName || '—'}</Typography>
                        )}
                    </Grid>
                </Grid>
            </Paper>

            <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>Описание задачи:</Typography>
                {isEditing ? (
                    <TextField
                        fullWidth
                        multiline
                        rows={6}
                        variant="outlined"
                        label="Описание задачи"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                    />
                ) : (
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>{task.description}</Typography>
                )}
            </Box>

            {task.requirements && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom>Требования:</Typography>
                    {isEditing ? (
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            variant="outlined"
                            label="Требования"
                            value={editRequirements}
                            onChange={(e) => setEditRequirements(e.target.value)}
                        />
                    ) : (
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>{task.requirements}</Typography>
                    )}
                </Box>
            )}

            <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>Результат выполнения:</Typography>
                {isEditing ? (
                    <TextField
                        fullWidth
                        multiline
                        rows={6}
                        variant="outlined"
                        label="Результат выполнения"
                        value={editCompletionResult}
                        onChange={(e) => setEditCompletionResult(e.target.value)}
                    />
                ) : (
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>{task.completionResult || '—'}</Typography>
                )}
            </Box>

            {task.reviewComment && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom>Комментарий куратора:</Typography>
                    {isEditing ? (
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            variant="outlined"
                            label="Комментарий куратора"
                            value={editReviewComment}
                            onChange={(e) => setEditReviewComment(e.target.value)}
                        />
                    ) : (
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>{task.reviewComment}</Typography>
                    )}
                </Box>
            )}

            <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>Исполнители:</Typography>
                <Stack direction="row" spacing={2} sx={{ overflowX: 'auto', pb: 1 }}>
                    {isEditing ? (
                        <FormControl fullWidth size="small">
                            <InputLabel id="assigned-users-label">Назначенные исполнители</InputLabel>
                            <Select
                                labelId="assigned-users-label"
                                multiple
                                value={selectedAssignedUserIds || []}
                                onChange={(event) => {
                                    const value = event.target.value as string[];
                                    setSelectedAssignedUserIds(value);
                                }}
                                input={<OutlinedInput label="Назначенные исполнители" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((userId) => {
                                            const user = availableUsers.find(u => u.id === userId);
                                            return user ? <Chip key={user.id} label={user.fullName} /> : null;
                                        })}
                                    </Box>
                                )}
                                MenuProps={MenuProps}
                            >
                                {availableUsers.map((user) => (
                                    <MenuItem key={user.id} value={user.id}>
                                        {user.fullName || user.personalData?.fullName || user.phone}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : (
                        <>
                            {task.taskUsers && task.taskUsers.length > 0 ? (
                                task.taskUsers.map(taskUser => (
                                    <Paper key={taskUser.id} elevation={1} sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 150 }}>
                                        <Avatar src={taskUser.user?.avatar} sx={{ width: 56, height: 56, mb: 1 }}>
                                            {taskUser.user?.fullName ? taskUser.user.fullName.split(' ').map(n => n[0]).join('') : (taskUser.user?.personalData?.fullName ? taskUser.user.personalData.fullName.split(' ').map(n => n[0]).join('') : '—')}
                                        </Avatar>
                                        <Typography variant="subtitle2">{taskUser.user?.fullName || taskUser.user?.personalData?.fullName || '—'}</Typography>
                                        <Typography variant="body2" color="textSecondary">{taskUser.status}</Typography>
                                    </Paper>
                                ))
                            ) : (
                                <Typography variant="body2" color="textSecondary">Исполнители не назначены.</Typography>
                            )}
                        </>
                    )}
                </Stack>
            </Box>

            <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>Навыки задачи:</Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {isEditing ? (
                        <FormControl fullWidth size="small">
                            <InputLabel id="skills-label">Навыки</InputLabel>
                            <Select
                                labelId="skills-label"
                                multiple
                                value={selectedSkillIds || []}
                                onChange={(event) => {
                                    const value = event.target.value as string[];
                                    setSelectedSkillIds(value);
                                }}
                                input={<OutlinedInput label="Навыки" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((skillId) => {
                                            const skill = availableSkills.find(s => s.id === skillId);
                                            return skill ? <Chip key={skill.id} label={skill.title} /> : null;
                                        })}
                                    </Box>
                                )}
                                MenuProps={MenuProps}
                            >
                                {availableSkills.map((skill) => (
                                    <MenuItem key={skill.id} value={skill.id}>
                                        {skill.title}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : (
                        <>
                            {skillsToDisplay.length > 0 ? (
                                skillsToDisplay.map(skill => <Chip key={skill.id} label={skill.title} />)
                            ) : (
                                <Typography variant="body2" color="textSecondary">Навыки не указаны.</Typography>
                            )}
                        </>
                    )}
                </Stack>
            </Box>

            <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>Медиафайлы:</Typography>
                <Stack spacing={1}>
                    {task.media && task.media.length > 0 ? (
                        task.media.map((mediaItem) => (
                            <Paper key={mediaItem.id} variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                {mediaItem.fileUrl.match(/\.(jpeg|jpg|gif|png|webp)$/i) ? (
                                    <img src={mediaItem.fileUrl} alt={mediaItem.name} style={{ width: 24, height: 24, objectFit: 'cover' }} />
                                ) : (
                                    <LinkIcon fontSize="small" />
                                )}
                                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                    <a href={mediaItem.fileUrl} target="_blank" rel="noopener noreferrer">{mediaItem.name}</a>
                                </Typography>
                            </Paper>
                        ))
                    ) : (
                        <Typography variant="body2" color="textSecondary">Файлы не загружены.</Typography>
                    )}
                </Stack>
            </Box>

            {isPlatformCuratorOrAdmin && isEditing && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                    <Button variant="outlined" startIcon={<Cancel />} onClick={handleCancelEdit}>Отмена</Button>
                    <Button variant="contained" startIcon={<Save />} onClick={handleSave}>Сохранить Изменения</Button>
                </Box>
            )}
        </Box>
    );
};

export default AdminTaskEntityDetailsPage;
